// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { type ApiErrorPayloadCommon, isApiError } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import { apiClient } from '../client';

interface IdentifyResponse {
  patientId: string;
}

const identifyPatientRequest = async (
  data: string,
  codingContext: CodingContext | undefined,
): Promise<string> => {
  const response = await apiClient.post<IdentifyResponse>(
    'identify',
    () => ({ photo: codingContext?.encryptString(data) }),
    codingContext,
  );
  return response.patientId;
};

export const useIdentifyPatientMutation = (options: {
  onSuccess: (patientId: string) => void;
}) => {
  const { codingContext } = usePlatform();
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | undefined>(undefined);
  const [isNetworkError, setIsNetworkError] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: string) => identifyPatientRequest(data, codingContext),
    onError(error) {
      if (isApiError(error)) {
        setApiError(error);
      } else {
        // Network failure, TypeError from fetch, or other non-API error
        setIsNetworkError(true);
        setApiError({ error: { code: 'network-error', message: 'Backend unreachable' } });
      }
    },
    onSuccess: (patientId) => options.onSuccess(patientId),
  });

  return {
    identifyPatient: mutation.mutate,
    apiError,
    isNetworkError,
    isPending: mutation.isPending,
  };
};
